import { DateTime, Interval } from "luxon";

import {
  type Delivery,
  DeliveryConfiguration,
  type DeliveryConfigurationJson,
} from "./delivery-configuration";
import { Sector, type SectorJson } from "./sector";
import { Show, type ShowJson } from "./show";
import { type Venue } from "./venue";

export type Gateway = {
  id: number;
  name?: string;
  type: string;
  paymentMethod: string;
  requiredCode: boolean | null;
  methods?: Array<{ id: number; name: string }>;
  credentials?: { storeId: string | undefined; cnpj: string | undefined };
  payFacEnabled?: boolean | null;
  /** SitefConfiguration.pixEnabled — habilita el pago PIX (carteira digital) por SiTef. */
  pixEnabled?: boolean | null;
  /** SitefConfiguration.pixReservationDuration — minutos de reserva mientras se espera el pago PIX. */
  pixReservationDuration?: number | null;
  /** MercadoPagoQrConfiguration / ModoQrConfiguration.expirationMinutes — minutos de vigencia del QR. */
  expirationMinutes?: number | null;
};

export type EventClient = {
  id: number;
  displayName: string | null;
  legalName: string | null;
  taxIdType: string | null;
  taxId: string | null;
  phoneNumber: string | null;
  phoneCountryCode: string | null;
  publicEmail: string | null;
  address: {
    street: string | null;
    streetNumber: string | null;
    city: string | null;
    province: string | null;
    zipCode: string | null;
    country: string | null;
  } | null;
};

type ShowRange = { from: DateTime; to: DateTime };

type SelectionType =
  | "CalendarSelection"
  | "MultiProductSelection"
  | "PerDateSelection"
  | "SingleProductSelection"
  | "SeatsV2Selection"
  | "MultiSeatsSelection"
  | "CalendarMultiSeatsSelection"
  | "SeatsSelection";

type PortalConfiguration = {
  id: number;
  enabled: boolean;
};
type EventJson = {
  id: number;
  channelId: number;
  name: string;
  description: string;
  thumbImage: string;
  image: string;
  currency: string;
  venue: Venue;
  deliveryConfiguration: DeliveryConfigurationJson[];
  shows: ShowJson[];
  combos?: SectorJson[];
  gateways: Gateway[] | null;
  noServiceCharge?: boolean;
  selectionType: SelectionType;
  showsRange?: { from: string; to: string };
  requireRequester?: boolean | null;
  requireCorporateClient?: boolean | null;
  portalConfiguration?: PortalConfiguration;
  facilityFee: FacilityFee | null;
  client?: EventClient | null;
};

type FacilityFee = {
  name: string | null;
  fee: number;
};

class Event {
  // private readonly id: number;
  private readonly name: string;
  private readonly channelId: number;
  private readonly selectionType: SelectionType;
  private readonly noServiceCharge: boolean;
  private readonly monthsRange: DateTime[];
  private readonly venue: Venue;
  private readonly gateways: readonly Gateway[];
  private readonly deliveryConfiguration: readonly DeliveryConfiguration[];
  private readonly shows: readonly Show[];
  private readonly requireRequester: boolean;
  private readonly requireCorporateClient: boolean;
  private readonly portalConfiguration: PortalConfiguration | undefined;
  readonly facilityFee: FacilityFee | null;
  private readonly client: EventClient | null;

  static fromJson = (json: EventJson) =>
    new Event(
      json.id,
      json.name,
      json.channelId,
      json.selectionType,
      json.noServiceCharge,
      json.showsRange?.from && json.showsRange.to
        ? { from: DateTime.fromISO(json.showsRange.from), to: DateTime.fromISO(json.showsRange.to) }
        : null,
      json.venue,
      json.gateways ?? [],
      DeliveryConfiguration.fromJsonList(json.deliveryConfiguration),
      json.shows
        .map((show) => Show.fromJson(show, json.noServiceCharge ?? false))
        .concat(
          json.combos && json.combos.length > 0
            ? new Show(
                "combo",
                "Abonos",
                json.combos.map((sector) => Sector.fromJson(sector, json.noServiceCharge)),
              )
            : [],
        ),
      json.requireRequester ?? false,
      json.requireCorporateClient ?? false,
      json.portalConfiguration,
      json.facilityFee,
      json.client ?? null,
    );

  constructor(
    _id: number,
    name: string,
    channelId: number,
    selectionType: SelectionType,
    noServiceCharge: boolean | undefined,
    showsRange: ShowRange | null,
    venue: Venue,
    gateways: Gateway[],
    deliveryConfiguration: DeliveryConfiguration[],
    shows: Show[],
    requireRequester: boolean,
    requireCorporateClient: boolean,
    portalConfiguration: PortalConfiguration | undefined,
    facilityFee: FacilityFee | null,
    client: EventClient | null = null,
  ) {
    // this.id = id;
    this.name = name;
    this.channelId = channelId;
    this.selectionType = selectionType;
    this.noServiceCharge = noServiceCharge ?? false;
    this.monthsRange = this.generateRangeMonths(showsRange);
    this.venue = venue;
    this.gateways = gateways;
    this.shows = shows.sort((s1, s2) => (s1.isBefore(s2) ? -1 : 1));
    this.deliveryConfiguration = deliveryConfiguration;
    this.requireRequester = requireRequester;
    this.requireCorporateClient = requireCorporateClient;
    this.portalConfiguration = portalConfiguration;
    this.facilityFee = facilityFee;
    this.client = client;
  }

  getName() {
    return this.name;
  }

  getCountry() {
    return this.venue.address.country;
  }

  getChannelId() {
    return this.channelId;
  }

  getDeliveryConfigurations() {
    return this.deliveryConfiguration;
  }

  isCalendarSelection() {
    return ["CalendarMultiSeatsSelection", "CalendarSelection"].includes(this.selectionType);
  }

  hasNoServiceCharge() {
    return this.noServiceCharge;
  }

  getRequireRequester() {
    return this.requireRequester;
  }

  getRequireCorporateClient() {
    return this.requireCorporateClient;
  }

  findShowById(id: string | number) {
    const show = this.shows.find((show) => show.isId(id));

    if (!show) throw new Error("Show not found");

    return show;
  }

  getUniqueShow() {
    return (this.shows.length === 1 ? this.shows[0] : null) ?? null;
  }

  mapShows<T>(mapper: (show: Show) => T) {
    return this.shows.map(mapper);
  }

  findDeliveryConfiguration(deliveryMethod: Delivery) {
    return this.deliveryConfiguration.find((d) => d.isType(deliveryMethod));
  }

  getShowsRangeFrom() {
    const date = this.monthsRange[0];

    if (!date) {
      throw new Error("Event has no upcoming shows");
    }

    return date;
  }

  getPortalConfigurationEnabled() {
    return this.portalConfiguration?.enabled ?? false;
  }

  getGateways() {
    return this.gateways;
  }

  getClient() {
    return this.client;
  }

  hasMap() {
    return [
      "SeatsSelection",
      "SeatsV2Selection",
      "MultiSeatsSelection",
      "CalendarMultiSeatsSelection",
    ].includes(this.selectionType);
  }

  private generateRangeMonths(showsRange: ShowRange | null) {
    if (!showsRange) {
      return [];
    }

    const from = (showsRange.from < DateTime.local() ? DateTime.local() : showsRange.from).startOf(
      "month",
    );
    const to = showsRange.to.startOf("month");

    if (to < from) {
      return [];
    }

    const diff = Interval.fromDateTimes(from, to).length("month") + 1;

    if (diff <= 0) {
      return [];
    }

    return [...Array(diff)].map((_, i) => from.plus({ month: i }));
  }

  mapRangeMonths<T>(mapper: (value: DateTime) => T) {
    return this.monthsRange.map(mapper);
  }
}

export type { EventJson };
export { Event };
